import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "../../lib/api";

const TYPES = ["all", "contact", "wholesale", "sample"];
const STATUSES = ["new", "replied", "closed"];
const badge = { new: "bg-[#b08a5f]/20 text-[#7c5230]", replied: "bg-[#1f4a33]/15 text-[#1f4a33]", closed: "bg-[#10271a]/10 text-[#5e6b61]" };

export default function EnquiriesPanel() {
  const [type, setType] = useState("all");
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/enquiries", { params: type === "all" ? {} : { type } });
    setItems(data);
  };
  useEffect(() => { load(); }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/enquiries/${id}`, { status });
      setItems((l) => l.map((e) => (e.id === id ? data : e)));
      toast.success(`Marked as ${status}`);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div data-testid="admin-enquiries">
      <p className="text-[#7c5230] text-xs tracking-[0.25em] uppercase">Trade desk</p>
      <h1 className="mt-3 font-display text-4xl md:text-5xl font-light text-[#10271a]">Enquiries</h1>
      <div className="mt-8 flex flex-wrap gap-2">
        {TYPES.map((t) => (
          <button key={t} type="button" onClick={() => setType(t)} data-testid={`filter-${t}`} className={`px-4 py-2 rounded-full text-[12px] tracking-[0.1em] uppercase font-semibold border transition-colors ${type === t ? "bg-[#1f4a33] border-[#1f4a33] text-[#f6f1e7]" : "border-[#10271a]/15 text-[#10271a]/70"}`}>{t}</button>
        ))}
      </div>
      <div className="mt-8 rounded-3xl border border-[#10271a]/10 bg-[#faf6ec] overflow-hidden">
        {items === null && <p className="p-8 text-[#5e6b61]">Loading...</p>}
        {items?.length === 0 && <p className="p-8 text-[#5e6b61]" data-testid="enquiries-empty">No enquiries yet.</p>}
        <ul className="divide-y divide-[#10271a]/10">
          {items?.map((e) => (
            <li key={e.id} className="p-5 md:p-6" data-testid={`enquiry-row-${e.reference}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <button type="button" onClick={() => setOpen(open === e.id ? null : e.id)} className="text-left flex-1 min-w-[240px]">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-xs text-[#7c5230]">{e.reference}</span>
                    <span className="text-[10px] tracking-[0.18em] uppercase text-[#5e6b61]">{e.type}</span>
                    <span className={`text-[10px] tracking-[0.14em] uppercase px-2.5 py-1 rounded-full ${badge[e.status || "new"]}`} data-testid={`status-${e.reference}`}>{e.status || "new"}</span>
                  </div>
                  <p className="mt-2 font-display text-2xl text-[#10271a] leading-tight">{title(e)}</p>
                  <p className="text-sm text-[#5e6b61] mt-1">{subtitle(e)} · {new Date(e.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</p>
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                  {STATUSES.filter((s) => s !== (e.status || "new")).map((s) => (
                    <button key={s} type="button" onClick={() => setStatus(e.id, s)} data-testid={`mark-${s}-${e.reference}`} className="px-3 py-1.5 rounded-full text-[11px] tracking-[0.1em] uppercase font-semibold border border-[#10271a]/15 text-[#10271a]/75 hover:border-[#1f4a33] hover:text-[#1f4a33]">Mark {s}</button>
                  ))}
                  {e.type === "wholesale" && (
                    <Link to={`/admin/offers/new/${e.id}`} data-testid={`create-offer-${e.reference}`} className="px-3 py-1.5 rounded-full text-[11px] tracking-[0.1em] uppercase font-semibold bg-[#1f4a33] text-[#f6f1e7] hover:bg-[#10271a]">{e.offer_id ? "New offer" : "Create offer"}</Link>
                  )}
                </div>
              </div>
              {open === e.id && <Details e={e} />}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const title = (e) => (e.type === "wholesale" ? e.details.company : e.type === "sample" ? `${e.lot_name} sample · ${e.name}` : e.name);
const subtitle = (e) => (e.type === "wholesale" ? `${e.details.name} · ${e.details.email} · ${e.details.country}` : `${e.email}${e.company ? ` · ${e.company}` : ""}`);

function Details({ e }) {
  return (
    <div className="mt-5 pt-5 border-t border-[#10271a]/10 text-sm text-[#10271a] space-y-2" data-testid="enquiry-details">
      {e.type === "contact" && <p className="whitespace-pre-wrap">{e.message}</p>}
      {e.type === "sample" && <><p>Country: {e.country}</p>{e.address && <p>Address: {e.address}</p>}{e.notes && <p>Notes: {e.notes}</p>}</>}
      {e.type === "wholesale" && (
        <>
          <ul className="list-disc pl-5">{e.lines.map((l, i) => <li key={i}>{l.commodity_name} — {l.grade} — {l.volume} {l.unit} — {l.frequency}</li>)}</ul>
          <p>{e.details.buyer_type} · {e.details.incoterm}{e.details.port ? ` · ${e.details.port}` : ""}{e.details.phone ? ` · ${e.details.phone}` : ""}</p>
          {e.details.notes && <p>Notes: {e.details.notes}</p>}
        </>
      )}
      <p className="text-xs text-[#5e6b61]">Email: owner {String(e.email_status?.owner || "pending").slice(0, 40)} · ack {String(e.email_status?.ack || "pending").slice(0, 40)}</p>
    </div>
  );
}
